import React, { useState } from "react";
import InputMask from "react-input-mask";
import Swal from "sweetalert2";
import { MdSearch } from "react-icons/md";
import { FaMapMarkerAlt, FaStore } from "react-icons/fa";

import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import styles from "./cepSearch.module.scss";

const formatCep = cep => {
  const digits = String(cep || "").replace(/\D/g, "").padStart(8, "0");
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const CepSearch = () => {
  const api = new BrComerceApiService();

  const [cep, setCep] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const search = async () => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) {
      Swal.fire({
        icon: "warning",
        title: "Atenção",
        text: "Informe um CEP completo (8 dígitos)."
      });
      return;
    }

    setSearching(true);
    setResult(null);
    setNotFound(false);

    try {
      const response = await api.makeHttpRequest({
        method: "POST",
        url: "/addressbook/getAddress",
        data: { zipCode: digits }
      });
      const data = response?.data || response;

      // Sem resultado na base local nem nos Correios: campos voltam vazios
      // ou com "undefined" (fallback online sem match).
      if (!data || !data.uf || data.uf === "undefined") {
        setNotFound(true);
      } else {
        setResult(data);
      }
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Houve um erro ao consultar o CEP. Tente novamente."
      });
    } finally {
      setSearching(false);
    }
  };

  const onKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      search();
    }
  };

  return (
    <div className={styles.searchCard}>
      <h5>Consultar CEP</h5>
      <p className={styles.hint}>
        Consulta na base DNE carregada na plataforma, com fallback online nos
        Correios quando o CEP não está na base local.
      </p>

      <div className={styles.searchRow}>
        <div className={styles.input}>
          <InputMask
            mask="99999-999"
            maskChar=""
            placeholder="Digite o CEP"
            value={cep}
            onChange={e => setCep(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>

        <button
          type="button"
          className={styles.btnSearch}
          disabled={searching}
          onClick={search}
        >
          <MdSearch />
          <span>{searching ? "Consultando..." : "Consultar"}</span>
        </button>
      </div>

      {notFound && (
        <div className={styles.notFound}>
          CEP não encontrado na base DNE nem na consulta online dos Correios.
        </div>
      )}

      {result && (
        <div className={styles.resultCard}>
          {result.retiradaEmAgencia && (
            <div className={styles.pickupBanner}>
              <FaStore size={20} />
              <div>
                <strong>Retirada em agência</strong>
                <p>
                  Este CEP não possui entrega domiciliar: objetos são
                  disponibilizados para retirada na unidade dos Correios cujo
                  endereço está abaixo.
                </p>
              </div>
            </div>
          )}

          <div className={styles.resultHeader}>
            <FaMapMarkerAlt size={18} />
            <h5>{formatCep(result.cep)}</h5>
          </div>

          <div className={styles.resultGrid}>
            <div className={styles.resultItem}>
              <span>Logradouro</span>
              <p>{result.logradouro || "-"}</p>
            </div>
            <div className={styles.resultItem}>
              <span>Bairro</span>
              <p>{result.bairro || "-"}</p>
            </div>
            <div className={styles.resultItem}>
              <span>Cidade</span>
              <p>{result.localidade || "-"}</p>
            </div>
            <div className={styles.resultItem}>
              <span>UF</span>
              <p>{result.uf || "-"}</p>
            </div>
            {!!result.complemento2 && (
              <div className={styles.resultItem}>
                <span>Complemento</span>
                <p>{result.complemento2}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CepSearch;
